/**
 * dnaPipeline — DNA double helix.
 *
 * A rotating double helix made of two strands. Each strand is a
 * sinusoidal path in 3D, projected to 2D. The strands are connected
 * by "rungs" (the base pairs) which pulse with audio.
 *
 * Looks like a chromosome rotating in 3D space.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DNAPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


// 3D position of strand at parameter t (t in [0, 1] along the helix).
// phase is the rotation around the helix axis (0..2pi).
vec3 strandPos(float t, float phase) {
  float angle = t * 18.0 + phase;
  float radius = 0.3;
  float yPos = (t - 0.5) * 1.6;
  return vec3(
    radius * cos(angle),
    yPos,
    radius * sin(angle)
  );
}

// Project a 3D point to 2D screen.
vec2 project(vec3 p) {
  // Rotate around Y axis with time.
  float angle = uTime * 0.6 + bass * 0.5;
  float ca = cos(angle), sa = sin(angle);
  vec3 rotated = vec3(ca * p.x - sa * p.z, p.y, sa * p.x + ca * p.z);
  // Simple projection.
  return rotated.xy * 1.5;
}

float distToSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background: deep cytoplasm with subtle texture.
  vec3 col = vec3(0.04, 0.06, 0.12);

  // Draw the two strands as continuous curves.
  const int N_SEGMENTS = 64;
  float minDistA = 100.0;
  float minDistB = 100.0;
  for (int i = 0; i < N_SEGMENTS; i++) {
    if (i >= N_SEGMENTS - 1) break;
    float t1 = float(i) / float(N_SEGMENTS);
    float t2 = float(i + 1) / float(N_SEGMENTS);
    vec3 aA = strandPos(t1, 0.0);
    vec3 bA = strandPos(t2, 0.0);
    vec3 aB = strandPos(t1, 3.14159265);  // opposite strand, 180° offset
    vec3 bB = strandPos(t2, 3.14159265);
    vec2 pA = project(aA);
    vec2 pB = project(bA);
    vec2 qA = project(bA);
    vec2 qB = project(bB);
    minDistA = min(minDistA, distToSeg(p, pA, qA));
    minDistB = min(minDistB, distToSeg(p, pB, qB));
  }
  // Strand A color (cyan-ish).
  vec3 colorA = vec3(0.3, 0.7, 0.95);
  vec3 colorB = vec3(0.95, 0.45, 0.85);
  col += colorA * exp(-minDistA * minDistA * 5000.0) * 1.5;
  col += colorB * exp(-minDistB * minDistB * 5000.0) * 1.5;

  // Rungs: vertical connections between the strands at periodic intervals.
  const int N_RUNGS = 16;
  for (int i = 0; i < N_RUNGS; i++) {
    float fi = float(i);
    float t = fi / float(N_RUNGS);
    // Pulse intensity: audio-driven, with per-rung phase.
    float pulse = 0.5 + 0.5 * sin(uTime * 4.0 + fi * 1.5);
    pulse *= 0.5 + bass * 0.5 + mid * 0.5;
    vec3 a = strandPos(t, 0.0);
    vec3 b = strandPos(t, 3.14159265);
    vec2 pa = project(a);
    vec2 pb = project(b);
    float dRung = distToSeg(p, pa, pb);
    col += vec3(0.95, 0.95, 0.6) * exp(-dRung * dRung * 1500.0) * pulse * 0.6;
  }

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeDNABands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeDNARealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createDNAPipeline(gl: WebGL2RenderingContext): DNAPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawDNA(
  gl: WebGL2RenderingContext,
  pipe: DNAPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
